import { ChartContainer, ChartConfig } from "web-design-system";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { formatAsCrore, formatCompact } from "creso-core/utils";

export interface ChartDataPoint {
  label: string;
  invested: number;
  gains: number;
}

interface CalculatorChartProps {
  data: ChartDataPoint[];
  labels?: { invested?: string; gains?: string };
}

const chartConfig = {
  invested: {
    label: "Invested",
    color: "var(--color-chart-1)",
  },
  gains: {
    label: "Gains",
    color: "var(--color-chart-4)",
  },
} satisfies ChartConfig;

export function CalculatorChart({ data, labels }: CalculatorChartProps) {
  const investedLabel = labels?.invested ?? "Invested";
  const gainsLabel = labels?.gains ?? "Gains";

  return (
    <div className="flex flex-col size-full">
      <ChartContainer config={chartConfig} className="flex-1 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{ left: 0, right: 0, top: 4, bottom: 0 }}
            barCategoryGap="20%"
          >
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11 }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tickFormatter={(v) => formatCompact(v, 1)}
              tick={{ fontSize: 10 }}
              axisLine={false}
              tickLine={false}
              width={52}
            />
            <Tooltip
              formatter={(value: number, name: string) => [
                formatAsCrore(value),
                name === "invested" ? investedLabel : gainsLabel,
              ]}
              labelClassName="font-semibold"
              contentStyle={{
                backgroundColor: "var(--card)",
                border: "none",
                borderRadius: "0.5rem",
                boxShadow: "0 0 0.5rem 0 rgba(0, 0, 0, 0.1)",
              }}
              cursor={{ fill: "var(--muted)", opacity: 0.3 }}
            />
            <Bar
              dataKey="invested"
              stackId="a"
              fill="var(--color-chart-1)"
              radius={[0, 0, 4, 4]}
            />
            <Bar
              dataKey="gains"
              stackId="a"
              fill="var(--color-chart-4)"
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </ChartContainer>
      <div className="flex items-center justify-center gap-5">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-2 rounded-xs bg-[var(--color-chart-1)]" />
          <span className="text-xs text-muted-foreground">{investedLabel}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-2 rounded-xs bg-[var(--color-chart-4)]" />
          <span className="text-xs text-muted-foreground">{gainsLabel}</span>
        </div>
      </div>
    </div>
  );
}
